import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { GameAudioService } from '../../../_services/game-audio.service';
import { ImmersiveGameBase } from '../_shared/immersive-game.base';
import { GlobalService } from 'src/app/_services/global.service';
import { MiniGameSession, MiniGameStudent } from '../../../interfaces/mini-game.interface';
import { PizzaRound, CuttingPizzaState } from '../../../interfaces/cutting-pizza.interface';
import { CuttingPizzaStageComponent } from './cutting-pizza-stage.component';

/**
 * Host shell for Cutting Pizza. Reads `CuttingPizzaState` from
 * `MiniGameService.session$`, drives the `<app-cutting-pizza-stage>`
 * (via the SERVE button → `stage.serve()`), and writes outcomes back
 * through `scoreCuttingPizzaRound` + `advanceCuttingPizzaRound`.
 *
 * Studio rules live here: the turn-intro card (announces the student and
 * gates the board), the productive-struggle loop (a wrong SERVE wobbles,
 * the plate resets, and after the first miss a "count the slices" hint
 * fades in — never the answer), and the kid-friendly pizza look.
 *
 * Kind failed-try feedback (§3.2): a wrong SERVE plays NO buzzer/negative SFX.
 * Instead a PERSISTENT Chef Nino element (`showChef`) fades in beside a white
 * speech bubble with a warm, growth-mindset line (rotating EN/AR copy that never
 * reveals the answer). The element is toggled by opacity only — never
 * created/destroyed — so it cannot flicker; a repeat miss swaps the line in place.
 *
 * Chrome: the shell renders its OWN bottom-right fullscreen button (`.fs-btn`)
 * styled to match the bottom-left "Select Game" button (§3.7) — it does NOT use
 * the shared `<app-fullscreen-toggle>` (a mismatched dark/gold pill).
 */
@Component({
  selector: 'app-cutting-pizza-game',
  templateUrl: './cutting-pizza-game.component.html',
  styleUrls: ['./cutting-pizza-game.component.scss']
})
export class CuttingPizzaGameComponent extends ImmersiveGameBase implements OnInit, OnDestroy {
  @ViewChild(CuttingPizzaStageComponent) stage?: CuttingPizzaStageComponent;

  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;

  hintVisible = false;
  showSuccess = false;
  showTurnCard = false;
  resolving = false;
  firing = false;
  lastXp = 0;

  // ── Kind failed-try feedback (Chef Nino) — theme §3.2 ────────────────
  // A PERSISTENT element (see template/SCSS) toggled purely by `showChef`
  // opacity; on a wrong serve it fades in with a warm, growth-mindset line
  // (never the answer). No buzzer/negative SFX plays on a miss.
  showChef = false;
  chefLine: { en: string; ar: string } = { en: '', ar: '' };
  private chefIndex = 0;
  private chefTimer: any = null;
  /** Rotating encouragement copy (cycles in order on repeat misses). */
  private readonly chefLines: { en: string; ar: string }[] = [
    { en: 'Nice try! Not quite yet — give it another go.', ar: 'محاولة حلوة! لسه مش مظبوطة — جرّب تاني.' },
    { en: 'So close! Take another look and try again.', ar: 'قربت أوي! بصّ كمان مرة وجرّب.' },
    { en: 'Good thinking! Adjust it a little and go for it.', ar: 'تفكيرك حلو! عدّلها شوية وكمّل.' },
    { en: "You've got this — one more try!", ar: 'انت قدّها — جرّب مرة كمان!' }
  ];

  private prevRound = -1;
  private pendingTimeouts: any[] = [];

  constructor(
    private miniGameService: MiniGameService,
    private gameAudioService: GameAudioService,
    private globalService: GlobalService
  ) {
    super();
  }

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(s => {
        this.session = s;
        const round = this.state?.currentRound ?? -1;
        if (round !== this.prevRound) {
          this.prevRound = round;
          this.resetRoundUi();
        }
      });

    this.miniGameService.lastResult$
      .pipe(takeUntil(this.destroy$))
      .subscribe(r => { if (r) this.lastXp = r.xpEarned; });
  }

  ngOnDestroy(): void {
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    if (this.chefTimer) { clearTimeout(this.chefTimer); this.chefTimer = null; }
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resetRoundUi(): void {
    this.hintVisible = false;
    this.showSuccess = false;
    this.resolving = false;
    this.firing = false;
    this.hideEncouragement(); // never carry the chef across a round change / advance
    if (this.student) {
      // Board is gated behind the turn-intro card — start the round clock
      // when the card is dismissed so reaction time excludes the card delay.
      this.showTurnCard = true;
      this.later(() => this.dismissTurnCard(), 1900);
    } else {
      this.showTurnCard = false;
      this.beginRoundTiming();
    }
  }

  dismissTurnCard(): void {
    if (!this.showTurnCard) return; // idempotent: manual tap + auto-timeout
    this.showTurnCard = false;
    this.hideEncouragement(); // never stack the chef under the turn card / on start
    this.beginRoundTiming();
  }

  private later(fn: () => void, ms: number): void {
    this.pendingTimeouts.push(setTimeout(fn, ms));
  }

  // ── Derived state ──────────────────────────────────────────────────

  get state(): CuttingPizzaState | null {
    return (this.session?.gameState as CuttingPizzaState) ?? null;
  }

  get round(): PizzaRound | null {
    const s = this.state;
    if (!s) return null;
    return s.rounds[s.currentRound] ?? null;
  }

  get currentRound(): number { return this.state?.currentRound ?? 0; }
  get totalRounds(): number { return this.state?.totalRounds ?? 0; }
  get roundDots(): number[] { return Array.from({ length: this.totalRounds }, (_, i) => i); }
  get solvedFlags(): boolean[] { return this.state?.solved ?? []; }

  get student(): MiniGameStudent | null {
    const s = this.state;
    if (!s || s.currentStudentId == null) return null;
    return this.session?.students.find(st => st.id === s.currentStudentId) ?? null;
  }

  get studentName(): string {
    const st = this.student;
    if (!st) return '';
    return (this.currLang === 'ar' ? st.name.ar : st.name.en || '').split(' ')[0];
  }

  // ── Order / equation pieces ────────────────────────────────────────

  get den(): number { return this.round?.den ?? 1; }
  get aNum(): number { return this.round?.aNum ?? 0; }
  get bNum(): number { return this.round?.bNum ?? 0; }
  get opSign(): string { return this.round?.op === '-' ? '−' : '+'; }

  // ── Controls ───────────────────────────────────────────────────────

  onServe(): void {
    if (this.resolving || this.firing || this.showTurnCard || !this.stage?.canServe) return;
    this.firing = true;
    this.hideEncouragement(); // next interaction clears any lingering chef bubble
    this.markInteraction(); // first SERVE = first submission (for reaction time)
    this.gameAudioService.play('assets/game-mode/sound-effects/fastSoundEffect.mp3');
    this.stage.serve();
  }

  onSolved(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.hideEncouragement(); // never stack the chef under the success overlay
    this.gameAudioService.play('assets/game-mode/sound-effects/correctSoundEffect.mp3');
    this.miniGameService.scoreCuttingPizzaRound(true, this.roundMeta(true));
    this.showSuccess = true;
    this.later(() => this.miniGameService.advanceCuttingPizzaRound(), 2400);
  }

  onMissed(): void {
    if (this.resolving) return;
    // Wrong serve is NOT the end — the board wobbles, the plate resets,
    // and after the first honest miss a "count the slices" hint fades in.
    // The answer count is never revealed.
    this.firing = false;
    this.markFailedTry();
    this.hintVisible = true;
    // No buzzer / negative SFX on a wrong try (§3.2): this runs on a shared
    // class screen and a fail sound invites peers to laugh. Instead Chef Nino
    // fades in with an encouraging line.
    this.showEncouragement();
  }

  /**
   * Show/refresh the Chef Nino encouragement (theme §3.2). The element is
   * PERSISTENT — only `showChef`/`chefLine` change, so it never flickers:
   * on a repeat miss we swap the line in place with `showChef` staying true
   * and just restart the auto-dismiss timer (no off→on toggle).
   */
  private showEncouragement(): void {
    this.chefLine = this.chefLines[this.chefIndex % this.chefLines.length];
    this.chefIndex++;
    this.showChef = true;
    if (this.chefTimer) clearTimeout(this.chefTimer);
    this.chefTimer = setTimeout(() => { this.showChef = false; }, 2000);
  }

  /** Dismiss the encouragement immediately (next interaction / overlay / round change). */
  private hideEncouragement(): void {
    if (this.chefTimer) { clearTimeout(this.chefTimer); this.chefTimer = null; }
    this.showChef = false;
  }

  skip(): void {
    if (this.resolving) return;
    this.resolving = true;
    this.miniGameService.scoreCuttingPizzaRound(false, this.roundMeta(false));
    this.later(() => this.miniGameService.advanceCuttingPizzaRound(), 250);
  }
}
